import React, { useMemo, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { submitWheel } from "../lib/api";
import { ArrowRight, Loader2, Check } from "lucide-react";

const CATS = ["beziehung", "gesundheit", "beruf", "finanzen", "familie", "sinn", "selbstwert", "spiritualitaet"];

const Lebensrad = () => {
  const { t, lang } = useLang();
  const [scores, setScores] = useState(() => Object.fromEntries(CATS.map((k) => [k, 5])));
  const [email, setEmail] = useState("");
  const [first, setFirst] = useState("");
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(false);

  const labels = useMemo(() => CATS.map((k) => t(`wheel.categories.${k}`)), [lang, t]);

  // Build SVG slices
  const cx = 220, cy = 220, max = 200;
  const slices = CATS.map((k, i) => {
    const start = (i / CATS.length) * Math.PI * 2 - Math.PI / 2;
    const end = ((i + 1) / CATS.length) * Math.PI * 2 - Math.PI / 2;
    const r = (scores[k] / 10) * max;
    const x1 = cx + Math.cos(start) * r;
    const y1 = cy + Math.sin(start) * r;
    const x2 = cx + Math.cos(end) * r;
    const y2 = cy + Math.sin(end) * r;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`;
  });

  const setScore = (k, v) => setScores({ ...scores, [k]: Math.max(0, Math.min(10, Number(v))) });

  const avg = (Object.values(scores).reduce((a, b) => a + b, 0) / CATS.length).toFixed(1);

  const onSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await submitWheel({ email, first_name: first, scores, language: lang });
      setSaved(r);
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-28 pb-32 min-h-screen" data-testid="wheel-page">
      <div className="container-sat max-w-6xl">
        <div className="overline mb-4">SAT · Lebensrad</div>
        <h1 className="headline-xl text-4xl md:text-6xl mb-6" data-testid="wheel-title">{t("wheel.title")}</h1>
        <p className="text-white/65 mb-12 max-w-2xl">{t("wheel.subtitle")}</p>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* SVG Wheel */}
          <div className="lg:col-span-7">
            <div className="glass rounded-2xl p-6 md:p-10 flex justify-center">
              <svg viewBox="0 0 440 440" className="w-full max-w-[500px]" data-testid="wheel-svg">
                {/* concentric guides */}
                {[0.2, 0.4, 0.6, 0.8, 1].map((f, i) => (
                  <circle key={i} cx={cx} cy={cy} r={max * f} fill="none" stroke="rgba(255,255,255,0.06)" />
                ))}
                {/* slice dividers */}
                {CATS.map((_, i) => {
                  const a = (i / CATS.length) * Math.PI * 2 - Math.PI / 2;
                  return <line key={i} x1={cx} y1={cy} x2={cx + Math.cos(a) * max} y2={cy + Math.sin(a) * max} stroke="rgba(255,255,255,0.07)" />;
                })}
                {/* slices */}
                {slices.map((d, i) => (
                  <path key={i} d={d} fill={i % 2 === 0 ? "rgba(212,175,55,0.55)" : "rgba(107,91,255,0.55)"} stroke="#D4AF37" strokeWidth="0.5" />
                ))}
                {/* labels */}
                {CATS.map((k, i) => {
                  const a = ((i + 0.5) / CATS.length) * Math.PI * 2 - Math.PI / 2;
                  const lx = cx + Math.cos(a) * (max + 20);
                  const ly = cy + Math.sin(a) * (max + 20);
                  return (
                    <text key={k} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.7)" fontSize="12" fontFamily="Montserrat">{labels[i]}</text>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Sliders + save */}
          <div className="lg:col-span-5 space-y-5">
            <div className="glass rounded-2xl p-6">
              <div className="flex justify-between mb-1 text-xs uppercase tracking-widest text-white/45">
                <span>{t("wheel.avg")}</span>
                <span className="text-[#D4AF37]">{avg}/10</span>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-1 bg-[#D4AF37]" style={{ width: `${(avg/10)*100}%` }}></div>
              </div>
            </div>

            {CATS.map((k, i) => (
              <div key={k} className="glass rounded-xl p-4" data-testid={`wheel-slider-${k}`}>
                <div className="flex justify-between mb-2">
                  <span className="text-white/80">{labels[i]}</span>
                  <span className="text-[#D4AF37] font-medium">{scores[k]}</span>
                </div>
                <input type="range" min="0" max="10" value={scores[k]} onChange={(e) => setScore(k, e.target.value)} className="w-full accent-[#D4AF37]" />
              </div>
            ))}

            {!saved && (
              <form onSubmit={onSave} className="glass rounded-2xl p-6 space-y-3" data-testid="wheel-form">
                <input className="sat-input" placeholder={t("contact.first_name")} value={first} onChange={(e) => setFirst(e.target.value)} />
                <input required type="email" className="sat-input" placeholder={t("contact.email")} value={email} onChange={(e) => setEmail(e.target.value)} />
                <button disabled={loading} className="btn-primary w-full justify-center" data-testid="wheel-save">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : (<>{t("wheel.save")} <ArrowRight size={16} /></>)}
                </button>
              </form>
            )}
            {saved && (
              <div className="glass rounded-2xl p-6 text-center" data-testid="wheel-saved">
                <Check className="mx-auto text-[#D4AF37] mb-2" />
                <div className="serif text-xl">Gespeichert · {t("wheel.weakest")}: <span className="text-[#D4AF37]">{t(`wheel.categories.${saved.weakest}`)}</span></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lebensrad;
